/**
 * CoachComplianceReport.js
 *
 * "Primary coach" is assigned per age category in Config > Age Categories
 * (age_categories.primary_coach_id) — that coach is accountable for every
 * active student whose date of birth currently resolves into that bracket.
 * A student counts as "never really assessed" if their latest score for
 * every indicator still carries the "Default assessment" marker stamped
 * by Performance Upload's "Apply Default Scores" tool — i.e. no coach has
 * entered a real score since. This report surfaces exactly who still
 * needs a real assessment, per coach, so admins can hold coaches
 * accountable for keeping skill ratings current.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  FaUserTie, FaSyncAlt, FaChevronDown, FaChevronUp,
  FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaClipboardList,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useTheme, getT, glassCard, getBrandColors, lighten } from "./ThemeContext";
import API_BASE from "./apiConfig";
import PageHeaderIcon from "./components/PageHeaderIcon";
import StatusBadge from "./components/StatusBadge";

const BASE = API_BASE;

function statusMeta(status) {
  const tone = status === "fully_assessed" ? "success" : status === "partially_assessed" ? "warning" : "danger";
  const label = status === "fully_assessed" ? "Assessed" : status === "partially_assessed" ? "Partial" : "Not Assessed";
  const Icon = status === "fully_assessed" ? FaCheckCircle : status === "partially_assessed" ? FaExclamationTriangle : FaTimesCircle;
  return { label, Icon, tone };
}

function rateColor(rate, T) {
  if (rate >= 80) return T.green;
  if (rate >= 50) return T.orange;
  return T.red;
}

function CoachCard({ coach, T, gc, isDark }) {
  const [open, setOpen] = useState(false);
  const color = rateColor(coach.compliance_rate, T);
  const needAttention = coach.partially_assessed + coach.not_assessed;

  return (
    <div style={{ ...gc, borderRadius: 14, padding: "18px 20px", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", cursor: "pointer" }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: "flex", alignItems: "center",
          justifyContent: "center", background: `linear-gradient(135deg,${T.yellow},#fff3a0)` }}>
          <FaUserTie size={18} color={T.blue} />
        </div>
        <div style={{ minWidth: 160 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: T.blue, fontFamily: T.font }}>{coach.coach_name || "Unknown Coach"}</div>
          <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.font }}>{coach.levels.join(", ")}</div>
        </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textMuted, marginBottom: 4, fontFamily: T.font }}>
            <span>Compliance</span>
            <span style={{ fontWeight: 800, color }}>{coach.compliance_rate}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 5, background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${coach.compliance_rate}%`, background: color, borderRadius: 5, transition: "width 0.3s" }} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 18, fontFamily: T.font }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#10b981" }}>{coach.fully_assessed}</div>
            <div style={{ fontSize: 10, color: T.textMuted }}>Assessed</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#d97706" }}>{coach.partially_assessed}</div>
            <div style={{ fontSize: 10, color: T.textMuted }}>Partial</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#dc2626" }}>{coach.not_assessed}</div>
            <div style={{ fontSize: 10, color: T.textMuted }}>Not Done</div>
          </div>
        </div>

        {needAttention > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", background: "rgba(220,38,38,0.10)",
            padding: "4px 10px", borderRadius: 20, fontFamily: T.font }}>
            {needAttention} need attention
          </span>
        )}
        {open ? <FaChevronUp color={T.textMuted} /> : <FaChevronDown color={T.textMuted} />}
      </div>

      {open && (
        <div style={{ marginTop: 16, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"}`, paddingTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                <th style={{ padding: "6px 8px" }}>Student</th>
                <th style={{ padding: "6px 8px" }}>Level</th>
                <th style={{ padding: "6px 8px" }}>Indicators Assessed</th>
                <th style={{ padding: "6px 8px" }}>Status</th>
                <th style={{ padding: "6px 8px" }}>Last Real Assessment</th>
              </tr>
            </thead>
            <tbody>
              {coach.students.map(s => {
                const meta = statusMeta(s.status);
                return (
                  <tr key={s.student_id} style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}` }}>
                    <td style={{ padding: "7px 8px", fontWeight: 600, color: T.textPrimary || T.blue }}>{s.student_name}</td>
                    <td style={{ padding: "7px 8px" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                        color: s.color_hex, background: s.color_hex + "22" }}>{s.level_name}</span>
                    </td>
                    <td style={{ padding: "7px 8px", color: T.textMuted }}>{s.indicators_assessed} / {s.indicators_total}</td>
                    <td style={{ padding: "7px 8px" }}>
                      <StatusBadge label={meta.label} tone={meta.tone} icon={<meta.Icon size={10} />} />
                    </td>
                    <td style={{ padding: "7px 8px", color: T.textMuted }}>{s.last_real_assessment || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UnassignedBanner({ levels, T, gc }) {
  if (!levels.length) return null;
  const totalStudents = levels.reduce((sum, l) => sum + l.students.length, 0);
  return (
    <div style={{ ...gc, borderRadius: 14, padding: "16px 20px", marginBottom: 20,
      border: "1.5px solid rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <FaExclamationTriangle color="#dc2626" />
        <div style={{ fontWeight: 800, color: "#dc2626", fontFamily: T.font, fontSize: 13.5 }}>
          {totalStudents} student{totalStudents !== 1 ? "s" : ""} have no primary coach assigned
        </div>
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, fontFamily: T.font, marginBottom: 6 }}>
        These students aren't included in any coach's compliance rate. Assign a Primary Coach for each age category in{" "}
        <b>Config Master → Age Categories</b> to start tracking them.
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {levels.map(l => (
          <span key={l.level_id ?? "none"} style={{ fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 12,
            color: l.color_hex, background: l.color_hex + "22", border: `1px solid ${l.color_hex}55` }}>
            {l.level_name} ({l.students.length})
          </span>
        ))}
      </div>
    </div>
  );
}

export default function CoachComplianceReport() {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${BASE}/reports/coach-compliance`)
      .then(r => r.json())
      .then(d => { setData(d); setError(null); })
      .catch(() => setError("Could not load the compliance report."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const coaches = data?.coaches || [];
  const unassignedLevels = data?.unassigned_levels || [];
  const totalStudents = coaches.reduce((s, c) => s + c.total_students, 0);
  const totalCompliant = coaches.reduce((s, c) => s + c.fully_assessed, 0);
  const overallRate = totalStudents ? Math.round((totalCompliant / totalStudents) * 1000) / 10 : 0;
  const needAttention = coaches.reduce((s, c) => s + c.partially_assessed + c.not_assessed, 0);

  return (
    <div style={{ padding: "20px 24px 60px", fontFamily: T.font }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <PageHeaderIcon icon={<FaClipboardList size={18} color={T.blue} />} />
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: T.blue }}>Coach Compliance Report</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
              Tracks whether each coach's assigned students have a real skill assessment, or are still stuck on the auto-applied default score
            </div>
          </div>
        </div>
        <button onClick={load} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10,
          border: `1.5px solid ${T.blue}55`, background: T.glass, color: T.blue, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
          <FaSyncAlt size={11} /> Refresh
        </button>
      </div>

      {loading && <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Loading…</div>}
      {error && <div style={{ padding: 20, color: "#dc2626" }}>{error}</div>}

      {!loading && !error && data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 22 }}>
            {[
              { label: "Coaches Tracked",      value: coaches.length,        color: T.blue },
              { label: "Students Tracked",     value: totalStudents,         color: T.blue },
              { label: "Overall Compliance",   value: `${overallRate}%`,     color: rateColor(overallRate, T) },
              { label: "Need Attention",       value: needAttention,         color: needAttention > 0 ? T.red : T.green },
            ].map(card => (
              <div key={card.label} style={{ ...gc, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{card.label}</div>
              </div>
            ))}
          </div>

          <UnassignedBanner levels={unassignedLevels} T={T} gc={gc} />

          {coaches.length === 0 ? (
            <div style={{ ...gc, borderRadius: 14, padding: 30, textAlign: "center", color: T.textMuted }}>
              No coach has an age category assigned to them yet. Set a Primary Coach per bracket in Config Master → Age Categories.
            </div>
          ) : (
            coaches.map(c => <CoachCard key={c.coach_id} coach={c} T={T} gc={gc} isDark={isDark} />)
          )}
        </>
      )}
    </div>
  );
}
